import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { makeTranslator } from "@/lib/i18n";
import { DEFAULT_LANGUAGE, detectBrowserLanguage } from "@/lib/localeDetection";

const ThemeContext = createContext(null);

const safeGetStorage = (key) => { try { return localStorage.getItem(key); } catch { return null; } };
const safeSetStorage = (key, val) => { try { localStorage.setItem(key, val); } catch { /* private browsing */ } };

export const ThemeProvider = ({ children }) => {
  // Tema scuro rimosso: l'app è sempre in chiaro. `setTheme` resta come no-op
  // per compatibilità con eventuali chiamanti residui.
  const theme = "light";
  const setTheme = () => {};
  const [language, setLanguageState] = useState(() => {
    const saved = safeGetStorage("aurea_lang");
    if (saved === "it" || saved === "en") return saved;
    return detectBrowserLanguage() || DEFAULT_LANGUAGE;
  });
  // A saved value is treated as an explicit choice. New visitors start in
  // the browser language and can still be refined by the login IP fallback.
  const [languageSource, setLanguageSource] = useState(() => {
    const saved = safeGetStorage("aurea_lang");
    return saved === "it" || saved === "en" ? "manual" : "auto";
  });

  const setLanguage = useCallback((next) => {
    const normalized = next === "it" ? "it" : "en";
    setLanguageState(normalized);
    setLanguageSource("manual");
    safeSetStorage("aurea_lang", normalized);
  }, []);

  const setAutoLanguage = useCallback((next) => {
    if (next !== "it" && next !== "en") return;
    setLanguageState(next);
  }, []);

  useLayoutEffect(() => {
    document.documentElement.classList.remove("dark");
  }, []);

  useEffect(() => {
    if (languageSource === "manual") safeSetStorage("aurea_lang", language);
    document.documentElement.lang = language;
  }, [language, languageSource]);

  const t = useMemo(() => makeTranslator(language), [language]);

  const value = useMemo(
    () => ({ theme, setTheme, language, languageSource, setLanguage, setAutoLanguage, t }),
    [language, languageSource, setLanguage, setAutoLanguage, t],
  );

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
export const useT = () => useContext(ThemeContext).t;
